/**
 * Pure keyframe builders for the named primitives (FIRST BLOOD §2.3), with
 * beat structures taken VERBATIM from the extracted comps where the comps
 * define the keyframe (packages/ui/tokens/TIMINGS.md — D-007: comps win).
 *
 * Every builder returns Web Animations API material: {keyframes, options}.
 * CSS fallbacks with the same values live in ../juice.css (jz-* classes).
 */

import { COMP_EASE, COMP_MS, DUR, EASE, REDUCED_CROSSFADE_MS } from "../tokens.js";

export interface PrimitiveAnimation {
  keyframes: Keyframe[];
  options: KeyframeAnimationOptions;
  /** CSS fallback class in juice.css carrying the same values. */
  cssClass: string;
}

export interface PrimitiveOpts {
  delayMs?: number;
  durationMs?: number;
}

/** POP — md-pop verbatim: scale .6 → 1.08 @70% → 1, back easing, 300ms
 *  (comps call md-pop at .3s/.4s; .3s is the dominant call site.
 *  FIRST BLOOD's T-quick/180 stands only where comps are silent — logged). */
export function pop(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "scale(.6)", opacity: 0 },
      { transform: "scale(1.08)", opacity: 1, offset: 0.7 },
      { transform: "scale(1)", opacity: 1 },
    ],
    options: {
      duration: opts.durationMs ?? COMP_MS.base,
      easing: COMP_EASE.back,
      delay: opts.delayMs ?? 0,
      fill: "both",
    },
    cssClass: "jz-pop",
  };
}

/** STAMP — md-stamp verbatim: scale 1.7 → .98 @70% → 1 + opacity, back
 *  easing, 340ms (comps win over FIRST BLOOD's 1.6→1.0 @ T-move).
 *  Pair with H-land at the call site — the stamp hits DOWN onto the surface. */
export function stamp(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "scale(1.7)", opacity: 0 },
      { transform: "scale(.98)", opacity: 1, offset: 0.7 },
      { transform: "scale(1)", opacity: 1 },
    ],
    options: {
      duration: opts.durationMs ?? COMP_MS.stamp,
      easing: COMP_EASE.back,
      delay: opts.delayMs ?? 0,
      fill: "both",
    },
    cssClass: "jz-stamp",
  };
}

/** RISE — md-rise verbatim: translateY 22px → 0 + opacity, ease-out, 300ms
 *  (comps: 22px @ .3s/.4s ease-out; FIRST BLOOD's 24px @ T-reveal E-enter
 *  stands only where comps are silent — logged). */
export function rise(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "translateY(22px)", opacity: 0 },
      { transform: "translateY(0)", opacity: 1 },
    ],
    options: {
      duration: opts.durationMs ?? COMP_MS.base,
      easing: "ease-out",
      delay: opts.delayMs ?? 0,
      fill: "both",
    },
    cssClass: "jz-rise",
  };
}

/** CASCADE stagger — RISE applied to siblings, 60ms apart, DOM order
 *  (stagger = scan order; order siblings in the intended reading sequence). */
export const CASCADE_STAGGER_MS = 60;

export function cascadeStep(index: number, opts: PrimitiveOpts = {}): PrimitiveAnimation {
  const base = rise(opts);
  return {
    ...base,
    options: { ...base.options, delay: (opts.delayMs ?? 0) + index * CASCADE_STAGGER_MS },
  };
}

/** BREATHE — scale 1 ↔ 1.03 loop (md-breathe), 1800ms, ease-in-out.
 *  THE one idle attractor per screen — never instantiate outside
 *  BreathingAttractor / the attractor registry. */
export function breathe(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "scale(1)" },
      { transform: "scale(1.03)", offset: 0.5 },
      { transform: "scale(1)" },
    ],
    options: {
      duration: opts.durationMs ?? COMP_MS.breathe,
      easing: "ease-in-out",
      delay: opts.delayMs ?? 0,
      iterations: Infinity,
    },
    cssClass: "jz-breathe",
  };
}

/** PULSE-RING — expanding stroke ring: scale .8 → 1.75, opacity .65 → 0
 *  (md-ring beat structure), 1200ms active, repeats every 2400ms
 *  (FIRST BLOOD cadence — comps are silent on the cadence).
 *  Applied to the overlay ring element, not the host. */
export function pulseRing(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "scale(.8)", opacity: 0.65 },
      { transform: "scale(1.75)", opacity: 0, offset: 0.5 },
      { transform: "scale(1.75)", opacity: 0 },
    ],
    options: {
      duration: opts.durationMs ?? 2400, // 1200ms ring + 1200ms rest
      easing: "ease-out",
      delay: opts.delayMs ?? 0,
      iterations: Infinity,
    },
    cssClass: "jz-ring",
  };
}

/** SHIMMER — md-shimmer verbatim: 45°-ish band (skewX(-18deg)) sweeping
 *  -160% → 220% occupying 72%→88% of a 5s loop, 2.5s initial delay.
 *  Decorative — never the focal attractor. OFF in TRIM/LITE. */
export function shimmer(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "translateX(-160%) skewX(-18deg)" },
      { transform: "translateX(-160%) skewX(-18deg)", offset: 0.72 },
      { transform: "translateX(220%) skewX(-18deg)", offset: 0.88 },
      { transform: "translateX(-160%) skewX(-18deg)" },
    ],
    options: {
      duration: opts.durationMs ?? 5000,
      easing: "ease-in-out",
      delay: opts.delayMs ?? 2500,
      iterations: Infinity,
    },
    cssClass: "jz-shimmer",
  };
}

/** SHAKE (rotational, crate grammar) — cs1/cs2/cs3 verbatim:
 *  ±3° 260ms · ±6° 260ms · ±9° 300ms, ease. Escalation index 1..3. */
export const SHAKE_ESCALATION = [
  { deg: 3, ms: 260, pattern: "single" },
  { deg: 6, ms: 260, pattern: "single" },
  { deg: 9, ms: 300, pattern: "double" },
] as const;

export function shakeRotate(step: 1 | 2 | 3, opts: PrimitiveOpts = {}): PrimitiveAnimation {
  const s = SHAKE_ESCALATION[step - 1] ?? SHAKE_ESCALATION[0];
  const sign = step === 2 ? -1 : 1; // cs2 rotates negative — comp verbatim
  const keyframes: Keyframe[] =
    s.pattern === "double"
      ? [
          { transform: "rotate(0)" },
          { transform: `rotate(${sign * s.deg}deg)`, offset: 0.33 },
          { transform: `rotate(${-sign * s.deg}deg)`, offset: 0.66 },
          { transform: "rotate(0)" },
        ]
      : [
          { transform: "rotate(0)" },
          { transform: `rotate(${sign * s.deg}deg)`, offset: 0.5 },
          { transform: "rotate(0)" },
        ];
  return {
    keyframes,
    options: { duration: opts.durationMs ?? s.ms, easing: "ease", delay: opts.delayMs ?? 0 },
    cssClass: `jz-shake-${step}`,
  };
}

/** SHAKE (translate, error grammar — FIRST BLOOD S1 edge):
 *  translateX ±6px, 3 oscillations, 240ms. */
export function shakeX(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "translateX(0)" },
      { transform: "translateX(-6px)", offset: 1 / 6 },
      { transform: "translateX(6px)", offset: 2 / 6 },
      { transform: "translateX(-6px)", offset: 3 / 6 },
      { transform: "translateX(6px)", offset: 4 / 6 },
      { transform: "translateX(-6px)", offset: 5 / 6 },
      { transform: "translateX(0)" },
    ],
    options: { duration: opts.durationMs ?? 240, easing: "linear", delay: opts.delayMs ?? 0 },
    cssClass: "jz-shake-x",
  };
}

/** GUTTER — brightness 1.0 ↔ 0.85 flicker at 8Hz for 300ms.
 *  Warnings/expiry ONLY. */
export function gutter(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  // 8Hz → toggle every 62.5ms → ~4.8 toggles across 300ms.
  return {
    keyframes: [
      { filter: "brightness(1)" },
      { filter: "brightness(.85)", offset: 0.208 },
      { filter: "brightness(1)", offset: 0.417 },
      { filter: "brightness(.85)", offset: 0.625 },
      { filter: "brightness(1)", offset: 0.833 },
      { filter: "brightness(.85)", offset: 0.96 },
      { filter: "brightness(1)" },
    ],
    options: { duration: opts.durationMs ?? 300, easing: "linear", delay: opts.delayMs ?? 0 },
    cssClass: "jz-gutter",
  };
}

/** MAGNET (source half) — after 600ms idle post-completion the completed
 *  element shrinks 4% and dims 10%. The attention handoff: the NEXT CTA
 *  wakes by mounting its own BreathingAttractor. Use at every completion. */
export const MAGNET_IDLE_MS = 600;

export function magnetRecede(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "scale(1)", opacity: 1 },
      { transform: "scale(.96)", opacity: 0.9 },
    ],
    options: {
      duration: opts.durationMs ?? DUR.move,
      easing: EASE.exit,
      delay: opts.delayMs ?? MAGNET_IDLE_MS,
      fill: "forwards",
    },
    cssClass: "jz-magnet",
  };
}

/** Screen kick — md-kick verbatim: y 0 → -7px @25% → 3px @55% → 0,
 *  280ms sharp. The ceremony impact frame. */
export function kick(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [
      { transform: "translate(0,0)" },
      { transform: "translate(0,-7px)", offset: 0.25 },
      { transform: "translate(0,3px)", offset: 0.55 },
      { transform: "translate(0,0)" },
    ],
    options: { duration: opts.durationMs ?? 280, easing: COMP_EASE.sharp, delay: opts.delayMs ?? 0 },
    cssClass: "jz-kick",
  };
}

/** Reduced-motion collapse — ALL primitives become this 200ms crossfade. */
export function reducedCrossfade(opts: PrimitiveOpts = {}): PrimitiveAnimation {
  return {
    keyframes: [{ opacity: 0 }, { opacity: 1 }],
    options: {
      duration: REDUCED_CROSSFADE_MS,
      easing: "ease-out",
      delay: opts.delayMs ?? 0,
      fill: "both",
    },
    cssClass: "jz-crossfade",
  };
}
